import { EApiLanguageCode } from '@datatype/iyagigo/api-type';
/** @jsxImportSource @emotion/react */

import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Scroll from '@component/common/scroll';
import * as size from '@constant/size';
import { IS_IYAGIGO_MOBILE_VIEW } from '@config/environment';
import * as color from '@constant/color';
import TextBlock from '@component/common/block/text-block';
import Gap from '@component/common/gap';
import HeaderProgress from '@component/iyagigo/common/header-progress';
import { useModifyTestWorkbookMutation, useTodayTestWorkbookQuery } from '@hook/iyagigo/useApi';
import { useTypedDispatch, useTypedSelector } from '@hook/common/useStore';
import IconTextBlock from '@component/common/block/icon-text-block';
import { actions as modalActions } from '@store/slice/modal-slice';
import { useNavigate } from 'react-router-dom';
import TextAudiosBlock from '@component/common/block/text-audios-block';
import { IYAGIGO_PATH } from '@constant/iyagigo/route-path';
import { normalizeChoice, normalizeInstruction, normalizePassage, normalizeQuestion } from '@util/string';
import ProblemChoice from '@component/iyagigo/test/problem/problem-choice';
import ProblemType from '@component/iyagigo/test/problem/problem-type';
import { getIsTodayStudied, getTestCodeFromCurrentStudy } from '@util/workbook';
import { TIyagigoSubMenu } from '@datatype/iyagigo/type';
import { actions as workbookActions } from '@store/slice/workbook-slice';
import { useActiveTime } from '@hook/common/useEvent';

// interface IProps {
//   mode: 'vocabulary' | 'grammar';
// }

// ############### variable ###############
// const GRAMMAR_SENTENCE_HEIGHT = '25px';
const TEXT_PADDING = '2px 5px';
const CARD_HEADER_HEIGHT = '40px';

const Wrapper = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  padding: ${size.PAGE_PADDING};
  display: flex;
  flex-flow: column nowrap;
`;

const ProblemCardContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  height: calc(100% - ${size.TEST_PROBLEM_NAVIGATOR_HEIGHT});
`;

const CardQuestionContainerA = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  padding: ${size.MARGIN_1};
  width: 100%;
`;

const CHOICE_CONTAINER_HEIGHT = '200px';
const QUESTION_CONTAINER_HEIGHT = '40px';

// 100%일때 부모요소 패딩을 뺀값이 100% 이므로, PAGE_PADDING은 계산식에 사용 X
const PassageContainer = styled(CardQuestionContainerA)`
  background-color: ${color.WHITE_1};
  padding: 0;
  max-height: calc(100% - ${QUESTION_CONTAINER_HEIGHT} - ${CHOICE_CONTAINER_HEIGHT});
  margin: 0 0 ${size.MARGIN_1} 0;
  border-radius: ${size.BORDER_RADIUS_1};
  border: ${size.BORDER_WIDTH_1} solid ${color.GRAY_1};
`;

const QuestionContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  margin: 0 0 ${size.MARGIN_1} 0;
  background-color: ${color.WHITE_1};
  // padding: 10px;
`;

const ChoiceContainer = styled(CardQuestionContainerA)`
  background-color: ${color.WHITE_1};
  padding: 0;
  max-height: ${CHOICE_CONTAINER_HEIGHT};
`;

const CardNavigationContainer = styled.div`
  position: absolute;
  bottom: 0;
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  height: ${size.TEST_PROBLEM_NAVIGATOR_HEIGHT};
`;

const ChoiceBox = styled.div`
  display: flex;
  width: 100%;
`;

const INSTRUCTION_TEXT_DESIGN = {
  text: { size: 'medium', color: color.BLACK_2, bold: true },
  box: { alignX: 'left', margin: '0 0 0 0' },
} as const;

const PASSAGE_TEXT_DESIGN = {
  text: { size: 'medium', color: color.BLACK_2, bold: false },
  box: {
    width: '100%',
    alignX: 'left',
    padding: size.MARGIN_1,
    // borderWidth: size.BORDER_WIDTH_1,
    // margin: `0 0 ${size.MARGIN_3} 0`,
  },
} as const;

const PROBLEM_TEXT_WIDTH = '40px';
const PROBLEM_TEXT_DESIGN = {
  text: { size: 'medium', color: color.BLACK_2, bold: false },
  box: {
    width: PROBLEM_TEXT_WIDTH,
    alignX: 'center',
    padding: '0 5px 0 5px',
    borderWidth: size.BORDER_WIDTH_1,
    // borderRadius: size.BORDER_RADIUS_1,
  },
} as const;

const QUESTION_TEXT_DESIGN = {
  text: { size: 'medium', color: color.BLACK_2, bold: true },
  // box: { alignX: 'left', width: `calc(100% - ${PROBLEM_TEXT_WIDTH} - 5px)` },
  box: { alignX: 'left' },
} as const;

// ############### design function ###############
const createScoringButtonDesign = (type: 'submit' | 'score', enable: boolean, isAnswer: boolean) => {
  const baseDesign = {
    text: { size: 'medium', color: color.WHITE_1 },
    box: {
      borderRadius: size.BORDER_RADIUS_2,
      backgroundColor: color.BROWN_1,
      height: size.DESIGN_SYSTEM_BUTTON_MEDIUM_HEIGHT,
      width: IS_IYAGIGO_MOBILE_VIEW ? '100%' : '200px',
      event: { cursor: 'pointer' },
    },
  } as const;

  if (type === 'submit') {
    if (enable) return baseDesign;
    return { ...baseDesign, box: { ...baseDesign.box, backgroundColor: color.GRAY_2 } };
  }

  return {
    ...baseDesign,
    icon: { size: 'medium', kind: 'outlined', color: color.WHITE_1 },
    box: { ...baseDesign.box, gap: '5px', backgroundColor: isAnswer ? color.GREEN_3 : color.RED_3 },
  } as const;
};

const createNavigationButtonDesign = (hide: boolean, disable: boolean) => {
  const baseDesign = {
    text: { size: 'medium', color: color.BROWN_1 },
    box: {
      padding: `5px 0px`,
      borderRadius: size.BORDER_RADIUS_1,
      borderWidth: size.BORDER_WIDTH_1,
      width: IS_IYAGIGO_MOBILE_VIEW ? '150px' : '70px',
      event: { cursor: 'pointer' },
      visibility: hide ? 'hidden' : 'visible',
    },
  } as const;

  return {
    ...baseDesign,
    text: { ...baseDesign.text, color: disable ? color.GRAY_2 : color.BROWN_1 },
    box: { ...baseDesign.box, borderColor: disable ? color.GRAY_2 : color.BROWN_1 },
  };
};

// ############### design ###############
// 진입전에 오늘 문제를 푸시겠어요 같은게 있어야 상태 저장 관리가 가능할듯 (cache store가 아닌 cloze store)
// 채점하기 클릭
// - 정답일 경우
//   - 문구변경 '채점하기' -> '정답이에요'
// - 오답일 경우
//   - 문구변경 '채점하기' -> '오답이에요'
// - 문구변경 '건너뛰기' -> '다음문제'
// - 마지막 문제일 경우
//   - 결과창 보여주기
//    - 문구변경 '건너뛰기' -> '끝내기'
// 끝내기 클릭
// - 결과창 보여주기
// 결과창 보여주기
// - '완료'상태로 변경
// 이전문제
// - 이전문제가기 없음
// 돌아가기 클릭
// - 홈으로 돌아가기
const Problem: React.FC<any> = () => {
  // ############### state ###############
  const dispatch = useTypedDispatch();
  const studyLanguage = useTypedSelector((state) => state.rootReducer?.studentReducer?.currentStudyLanguage) ?? null;
  const isAuthenticated = useTypedSelector((state) => state.rootReducer?.userReducer?.isAuthenticated) ?? false;
  const isAnswerView = useTypedSelector((state) => state.rootReducer?.workbookReducer?.isTestAnswerView) ?? false;
  const isComplete = useTypedSelector((state) => state.rootReducer?.workbookReducer?.isTestComplete) ?? false;
  const workbookIdx = useTypedSelector((state) => state.rootReducer?.workbookReducer?.testWorkbookIdx) ?? null;
  const score = useTypedSelector((state) => state.rootReducer?.workbookReducer?.testScore) ?? null;
  const currentStudyLanguage =
    useTypedSelector((state) => state.rootReducer?.studentReducer?.currentStudyLanguage) ?? null;
  const currentToeicTest = useTypedSelector((state) => state.rootReducer?.studentReducer?.currentToeicTest) ?? null;
  const currentJlptTest = useTypedSelector((state) => state.rootReducer?.studentReducer?.currentJlptTest) ?? null;
  const currentTest = currentStudyLanguage === EApiLanguageCode.ENGLISH ? currentToeicTest : currentJlptTest;
  useEffect(() => {
    if (!currentTest) return;
    dispatch(workbookActions.initialize());
    dispatch(workbookActions.storeIsStudying(true));
  }, [currentTest]);
  const { getActiveTime, reset: resetActiveTime } = useActiveTime(true);

  // ############### route ###############
  const navigate = useNavigate();

  // ############### api ###############
  const testCode = getTestCodeFromCurrentStudy(currentTest);
  const query = useTodayTestWorkbookQuery(testCode);
  const isLoading = query.isLoading;
  const data = query.data?.data || [];
  const notStudiedWorkbook = query.data?.data.filter((item) => !getIsTodayStudied(item.todayStudiedAt ?? '')) || [];
  const mutation = useModifyTestWorkbookMutation(testCode);
  const initialWorkbookIdx = data.length - notStudiedWorkbook.length;
  useEffect(() => {
    // 데이터 로드가 완료된 후에만 workbookIdx 설정
    if (!isLoading && data.length > 0) {
      dispatch(workbookActions.storeTestWorkbookIdx(initialWorkbookIdx));
    }
  }, [isLoading, initialWorkbookIdx, data.length, dispatch]);

  // ############### handler ###############
  const handleNavigationButtonClick = async () => {
    const studyTime = getActiveTime();
    if (isAnswer) {
      await mutation.mutateAsync({
        workbookKey: currentWorkbook.key,
        isCorrect: true,
        studyTime: studyTime,
      });
    } else {
      await mutation.mutateAsync({
        workbookKey: currentWorkbook.key,
        isCorrect: false,
        studyTime: studyTime,
      });
    }

    if (isComplete) {
      navigate(IYAGIGO_PATH.INITIAL_SERVICE_PATH);
      return;
    }
    if (isLastCloze) {
      dispatch(workbookActions.storeIsStudying(false));
      dispatch(workbookActions.storeIsTestComplete(true));
      dispatch(
        modalActions.showDialogModal({
          type: 'problem-score',
          data: { problemType: problemType, score: score, totalScore: data.length },
        }),
      );
      return;
    }
    dispatch(workbookActions.storeTestWorkbookIdx(workbookIdx + 1));
    dispatch(workbookActions.storeIsTestAnswerView(false));
    resetActiveTime();
  };

  const handleScoringButtonClick = () => {
    // mode에 따라 답변 여부 확인
    const hasAnswer = !!selectedAnswer;

    if (!hasAnswer) return;
    if (isAnswer) {
      dispatch(workbookActions.storeTestScore(score + 1));

      // 통합 스토어에 기록 저장 (회원/비회원 공용)
      dispatch(
        workbookActions.storeTestStudyRecord({
          workbookKey: currentWorkbook.key,
          isCorrect: true,
          todayStudiedAt: new Date().toISOString(),
        }),
      );
    } else {
      // 통합 스토어에 기록 저장 (회원/비회원 공용)
      dispatch(
        workbookActions.storeTestStudyRecord({
          workbookKey: currentWorkbook.key,
          isCorrect: false,
          todayStudiedAt: new Date().toISOString(),
        }),
      );
    }
    dispatch(workbookActions.storeIsTestAnswerView(true));

    if (isLastCloze) {
      dispatch(workbookActions.storeIsTestComplete(true));
      dispatch(
        modalActions.showDialogModal({
          type: 'problem-score',
          data: { problemType: problemType, score: score, totalScore: data.length },
        }),
      );
    }
  };

  const handleExplanationButtonClick = () => {
    dispatch(
      modalActions.showDialogModal({
        type: 'problem-explanation',
        data: {
          passage: currentWorkbook?.problem?.passage,
          passageAudioLink: currentWorkbook?.problem?.passageAudioLink,
          passageTranslation: currentWorkbook?.problem?.passageTranslation,
          questionA: currentWorkbook?.problem?.questionA,
          questionAAudioLink: currentWorkbook?.problem?.questionAAudioLink,
          questionATranslation: currentWorkbook?.problem?.questionATranslation,
          explanationA: currentWorkbook?.problem?.explanationA,
          vocabulary: currentWorkbook?.problem?.vocabulary,
          grammar: currentWorkbook?.problem?.grammar,
          hanja: currentWorkbook?.problem?.hanja,
        },
      }),
    );
  };

  // ############### function ###############

  // ############### variable ###############
  const problemType = studyLanguage === EApiLanguageCode.ENGLISH ? 'toeic' : 'jlpt';
  // ############### etc ###############
  const currentWorkbook = data[workbookIdx];
  const selectedAnswer =
    useTypedSelector(
      (state) => state.rootReducer?.workbookReducer?.selectedTestWorkbookChoice?.[currentWorkbook?.key],
    ) || null;
  const isLastCloze = workbookIdx === data.length - 1;

  // const selectedAnswerA = selectedAnswers['A'] ?? null;
  // const selectedAnswerB = selectedAnswers['B'] ?? null;
  // const selectedAnswerC = selectedAnswers['C'] ?? null;
  // const selectedAnswerD = selectedAnswers['D'] ?? null;
  // const selectedAnswerE = selectedAnswers['E'] ?? null;
  const normalizedInstruction = normalizeInstruction(currentWorkbook?.problem?.instruction);
  const normalizedPassage = normalizePassage(problemType, currentWorkbook?.problem?.passage);
  const normalizedQuestionA = normalizeQuestion(problemType, currentWorkbook?.problem?.questionA);
  // const normalizedQuestionB = normalizeQuestion(problemType, currentProblem?.problem?.questionB);
  // const normalizedQuestionC = normalizeQuestion(problemType, currentProblem?.problem?.questionC);
  // const normalizedQuestionD = normalizeQuestion(problemType, currentProblem?.problem?.questionD);
  // const normalizedQuestionE = normalizeQuestion(problemType, currentProblem?.problem?.questionE);

  // const normalizedChoiceA = normalizeChoice(problemType, currentProblem?.choiceA);
  // const normalizedChoiceB = normalizeChoice(problemType, currentProblem?.choiceB);
  // const normalizedChoiceC = normalizeChoice(problemType, currentProblem?.choiceC);
  // const normalizedChoiceD = normalizeChoice(problemType, currentProblem?.choiceD);
  // const normalizedChoiceE = normalizeChoice(problemType, currentProblem?.choiceE);

  // ###################################
  // 정답 판단
  // - 빈칸채우기(어휘)면 answer 비교
  // - 빈칸채우기(문법)면 sentence 비교 (특수문자 제거 후 비교)
  // ###################################
  const isAnswer = true; //= mode === 'vocabulary' ? getIsVocabularyAnswer() : getIsGrammarAnswer();

  if (!data.length || !currentWorkbook) {
    return <div></div>;
  }

  const code = currentWorkbook.problem.test.code;
  const isListeningProblem =
    code.startsWith('T113') ||
    code.startsWith('T123') ||
    code.startsWith('T133') ||
    code.startsWith('T143') ||
    code.startsWith('T153') ||
    code.startsWith('T212') ||
    code.startsWith('T222') ||
    code.startsWith('T232') ||
    code.startsWith('T242') ||
    code.startsWith('T252') ||
    code.startsWith('T214') ||
    code.startsWith('T224') ||
    code.startsWith('T234') ||
    code.startsWith('T244') ||
    code.startsWith('T254');

  let sources: string[] = [];
  let isPassageView: boolean;
  let isQuestionView: boolean;
  let isChoiceView: boolean;

  if (
    // toeic part2
    code.startsWith('T212') ||
    code.startsWith('T222') ||
    code.startsWith('T232') ||
    code.startsWith('T242') ||
    code.startsWith('T252')
  ) {
    isChoiceView = false;
    isQuestionView = false;
    isPassageView = false;
  } else if (
    // jlpt 글의 문법
    code.startsWith('T1113') ||
    code.startsWith('T1213') ||
    code.startsWith('T1313') ||
    code.startsWith('T1413') ||
    code.startsWith('T1513') ||
    // toeic part6
    code.startsWith('T216') ||
    code.startsWith('T226') ||
    code.startsWith('T236') ||
    code.startsWith('T246') ||
    code.startsWith('T256')
  ) {
    isChoiceView = true;
    isQuestionView = false;
    isPassageView = true;
  } else {
    isChoiceView = true;
    isQuestionView = true;
    isPassageView = true;
  }

  if (isPassageView) {
    sources = [
      currentWorkbook.problem.passageAudioLink,
      currentWorkbook.problem.questionAAudioLink,
      // currentProblem.questionBAudioLink,
      // currentProblem.questionCAudioLink,
      // currentProblem.questionDAudioLink,
      // currentProblem.questionEAudioLink,
    ].filter((item) => !!item);
  } else {
    sources = [currentWorkbook.problem.questionAAudioLink, currentWorkbook.problem.choiceAAudioLink].filter(
      (item) => !!item,
    );
  }

  return (
    <Wrapper>
      <ProblemCardContainer>
        {/* <HeaderProgress currentProgress={isAuthenticated ? initialWorkbookIdx : workbookIdx} totalProgress={data.length} /> */}
        {/* <Gap height={size.MARGIN_2} /> */}
        {/* <CardQuestionContainerA>
          <TextBlock text={normalizedInstruction} design={INSTRUCTION_TEXT_DESIGN} />
          {isListeningProblem && (
            <TextAudiosBlock
              sources={sources}
              timing={{
                begin: 1000,
                gap: 3000,
                end: 1000,
              }}
              replay={false}
              design={{
                text: { size: 'tiny', color: color.WHITE_1 },
                box: {
                  alignX: 'center',
                  width: '50px',
                  padding: '2px 0px',
                  margin: `${size.MARGIN_1} 0 0 0`,
                  backgroundColor: color.BROWN_1,
                  borderRadius: size.BORDER_RADIUS_1,
                  event: { cursor: 'pointer' },
                },
              }}
            />
          )}
        </CardQuestionContainerA> */}
        {currentWorkbook.problem.passage && !isListeningProblem && (
          <PassageContainer>
            <Scroll direction='y' height='100%' showScrollIndicator={true}>
              <TextBlock text={normalizedPassage} design={PASSAGE_TEXT_DESIGN} />
            </Scroll>
          </PassageContainer>
        )}
        <QuestionContainer>
          {/* <TextBlock text={`문제`} design={PROBLEM_TEXT_DESIGN} /> */}
          {isListeningProblem && (
            <TextAudiosBlock
              sources={sources}
              timing={{
                begin: 1000,
                gap: 2000,
                end: 1000,
              }}
              replay={false}
              design={{
                text: { size: 'tiny', color: color.WHITE_1 },
                box: {
                  alignX: 'center',
                  width: '50px',
                  padding: '2px 0px',
                  margin: `0 ${size.MARGIN_1} 0 0`,
                  backgroundColor: color.BROWN_1,
                  borderRadius: size.BORDER_RADIUS_1,
                  event: { cursor: 'pointer' },
                },
              }}
            />
          )}
          <TextBlock
            text={
              isListeningProblem
                ? isAnswerView || isQuestionView
                  ? normalizedQuestionA
                  : 'Listen carefully to the audio.'
                : isQuestionView
                  ? normalizedQuestionA
                  : ''
            }
            design={QUESTION_TEXT_DESIGN}
          />
        </QuestionContainer>
        <ChoiceContainer>
          <Scroll direction='y' maxHeight={'100%'} showScrollIndicator={true}>
            <ChoiceBox>
              <ProblemChoice
                workbookKey={currentWorkbook.key}
                choice={currentWorkbook.problem.choiceA}
                answer={currentWorkbook.problem.answerA}
                label={studyLanguage === EApiLanguageCode.ENGLISH ? 'alphabet' : 'number'}
                isAnswerView={isAnswerView}
                isChoiceView={isChoiceView}
              />
            </ChoiceBox>
          </Scroll>
        </ChoiceContainer>
      </ProblemCardContainer>
      <CardNavigationContainer>
        {isAnswerView ? (
          <IconTextBlock
            icon={isAnswer ? 'circle' : 'close'}
            text={isAnswer ? '정답이에요' : '오답이에요'}
            arrangement={'text-icon'}
            design={createScoringButtonDesign('score', true, isAnswer)}
          />
        ) : (
          <TextBlock
            text={'채점하기'}
            design={createScoringButtonDesign('submit', !!selectedAnswer && !isComplete, isAnswer)}
            click={{ handler: handleScoringButtonClick }}
          />
        )}
        <Gap width={size.MARGIN_1} />
        <TextBlock
          text={isComplete ? '돌아가기' : isLastCloze ? '끝내기' : isAnswerView ? '다음문제' : '건너뛰기'}
          design={createNavigationButtonDesign(false, false)}
          click={{ handler: handleNavigationButtonClick }}
        />
        <Gap width={size.MARGIN_1} />
        <TextBlock
          text={'해설보기'}
          design={createNavigationButtonDesign(!isAnswerView, false)}
          click={{ handler: handleExplanationButtonClick }}
        />
      </CardNavigationContainer>
    </Wrapper>
  );
};

export default Problem;
